import { Injectable } from '@angular/core';
import { Error } from 'fl-foundation';
import { ResourceVm } from 'qp-ui-interface';
import { removeEmptyOrUndefined } from 'qp-utility';
import { ErrorEvents } from '../../../shared/models/ErrorEvents';
import { getEmptyResource } from '../../../shared/utils/EventUtlis';
import { EventName } from '../enums/EventName';
import { NewrelicContentStore } from '../store/newrelic-content.store';
import { NewrelicStore } from '../store/newrelic.store';
import { getSubtitleInfo } from '../utils/playbackEventUtils';
import { LocalStorageService } from '../../../shared/services/local-storage/local-storage.service';

declare const window: Window &
  typeof globalThis & {
    enableNR: boolean;
    newrelic: any;
  };

@Injectable({
  providedIn: 'root',
})
export class NewrelicService {
  constructor(private newrelicStore: NewrelicStore, private newrelicContentStore: NewrelicContentStore, private readonly localStorage: LocalStorageService) {}

  get store(): NewrelicStore {
    return this.newrelicStore;
  }

  public sendError = (error: Error, errorType: ErrorEvents, resource?: ResourceVm, attributes: object = {}): void => {
    attributes = {
      isFreePreview: !!this.newrelicContentStore.getContentDetails()?.freePreview,
      ccEnabled: getSubtitleInfo(undefined, this.localStorage).ccEnabled,
      ...getEmptyResource(this.localStorage),
      ...this.newrelicContentStore.getTabInfo(),
      ...this.newrelicContentStore.getContainerInfo(),
      ...this.getCollectionInfo(EventName.ERROR),
      ...this.newrelicStore.getErrorInfo(error, errorType), // ERROR DATA
      ...attributes,
    };
    this.sendNewrelicEvent(EventName.ERROR, resource, attributes);
  };

  /**
   * Crafts a NewRelic event and sends it.
   * Depends on sendEventToNewRelic which sends the event to New Relic.
   * This function builds the Invalid X-device-id error in player.
   *
   * @param eventName
   * @param resource
   * @param customAttr
   * **/
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/ban-types
  public sendNewrelicEvent(eventName: EventName, resource?: ResourceVm, customAttributes: object = {}): void {
    try {
      this.newrelicContentStore.setContentDetails(resource);
      // X-device-id error is built here the actual payload is NRFactoryEvents...
      let attributes: any = {
        ...this.newrelicStore.getApplicationInfo(),
        ...this.newrelicStore.getDeviceInfo(),
        ...this.newrelicStore.getUserInfo(),
        ...this.getLanguageInfo(eventName),
        ...this.getSilentLogin(eventName),
        ...this.getSubscriptionInfo(eventName),
        ...customAttributes,
      };
      // Resource data
      if (this.newrelicContentStore.getContentDetails()) {
        attributes = {
          ...this.newrelicContentStore.getTabInfo(),
          ...this.newrelicContentStore.getContainerInfo(),
          ...this.getContentInfo(eventName),
          ...this.getCollectionInfo(eventName),
          ...attributes,
        };
      }
      //console.log('[NR]', eventName, attributes);
      this.sendEventToNewRelic(eventName, removeEmptyOrUndefined(attributes));
    } catch (e) {
      console.log('[NR]', e);
    }
  }

  /**
   * Sends a built event to NewRelic
   *
   * @param eventName
   * @param attributes
   * **/
  private sendEventToNewRelic(_: EventName, _2: any): void {
    // try {
    //   if (window.enableNR && window.newrelic) {
    //     // avoid send bg and fg events for Tizen and LG
    //     if (getDeviceType() !== DeviceTypeModel.LG && [EventName.APP_BACKGROUND, EventName.APP_FOREGROUND].includes(eventName)) return;
    //     window.newrelic.addPageAction(eventName, attributes);
    //     // console.log('[NR]', eventName, attributes);
    //   }
    // } catch (err) {
    //   console.log('ERROR NEW RELIC: ', err);
    // }
  }

  /**
   * return languages info for all except some events
   *
   * @param eventName
   * @returns
   */
  private getLanguageInfo(eventName: EventName) {
    if (eventName === EventName.LOGIN) return {};
    return this.newrelicStore.getLanguageInfo();
  }

  /**
   * return silentLogin info for all except some events
   *
   * @param eventName
   * @returns
   */
  private getSilentLogin(eventName: EventName) {
    if ([EventName.LINK_TO_EMAIL, EventName.LINK_TO_MOBILE].includes(eventName)) return {};
    return this.newrelicStore.getLoginInfo();
  }

  /**
   * return baseSubscription info for all except some events
   *
   * @param eventName
   * @returns
   */
  private getSubscriptionInfo(eventName: EventName) {
    if ([EventName.VIEW_PLANS, EventName.SUBSCRIPTION_INITIATED, EventName.PAYMENT_INITIATED].includes(eventName)) return {};
    return this.newrelicStore.getSubscriptionInfo();
  }

  /**
   * return content without seasonNumber and episodeNumber for all except some events
   *
   * @param eventName
   * @returns
   */
  private getContentInfo(eventName: EventName) {
    const details = this.newrelicContentStore.getContentInfo();
    if (
      ![EventName.RENT_CONTENT, EventName.FAVORITE_CONTENT, EventName.VOTE_NOW, EventName.NETWORK_CHANGE, EventName.ERROR].includes(
        eventName
      )
    ) {
      delete details.seasonNumber;
      delete details.episodeNumber;
    }
    return details;
  }

  /**
   * return collection info for some events
   *
   * @param eventName
   * @returns
   */
  private getCollectionInfo(eventName: EventName) {
    if (
      [
        EventName.FAVORITE_CONTENT,
        EventName.VIEW_COLLECTION_DETAILS,
        EventName.VOTE_NOW,
        EventName.NETWORK_CHANGE,
        EventName.ERROR,
        EventName.BANNER_AD_CLICK,
      ].includes(eventName)
    ) {
      return this.newrelicContentStore.getCollectionInfo();
    }
    return {};
  }
}
